import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import ConfirmDialog from '@/components/feature/ConfirmDialog';
import { useToast } from '@/contexts/ToastContext';
import {
  fixedAssetsApi,
  MODE_LABEL,
  type AssetDetailResponse,
  type DisposalInput,
  type DisposalPreview,
  type MaintenanceInput,
} from '@/api/fixedAssetsApi';
import { bankReconApi, type CashBankLedger } from '@/api/bankReconApi';
import { getSuppliers, type PartyResponse } from '@/api/party.api';
import {
  EntryPreview,
  Field,
  GstFields,
  Modal,
  StatusBadge,
  cardCls,
  dangerBtn,
  errMsg,
  fmtDate,
  inputCls,
  money,
  primaryBtn,
  secondaryBtn,
  thCls,
  todayStr,
} from '../components/ui';

type Tab = 'overview' | 'depreciation' | 'maintenance' | 'history' | 'accounting';

const emptyMaintenance: MaintenanceInput = {
  maintenanceType: 'REVENUE', maintenanceDate: todayStr(), workCategory: 'Routine service', description: '', amount: 0,
  gstType: 'NONE', gstAmount: 0, itcEligible: true, paymentMode: 'BANK', lifeExtensionYears: 0, downtimeDays: 0, markUnderMaintenance: false,
};

const WORK_CATEGORIES = {
  REVENUE: ['Routine service', 'Spare part replacement', 'Breakdown repair', 'Cleaning / lubrication', 'Calibration', 'Minor adjustment'],
  CAPITAL: ['Major overhaul', 'Capacity enhancement', 'Life extension', 'New component added', 'Upgrade'],
};

export default function FixedAssetDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const [data, setData] = useState<AssetDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Tab>('overview');
  const [ledgers, setLedgers] = useState<CashBankLedger[]>([]);
  const [suppliers, setSuppliers] = useState<PartyResponse[]>([]);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState<{ title: string; message: string; onConfirm: () => void } | null>(null);

  const [transferOpen, setTransferOpen] = useState(false);
  const [transfer, setTransfer] = useState({ department: '', locationDetail: '', custodianName: '', transferRemarks: '', movementDate: todayStr() });
  const [maintOpen, setMaintOpen] = useState(false);
  const [maint, setMaint] = useState<MaintenanceInput>(emptyMaintenance);
  const [disposeOpen, setDisposeOpen] = useState(false);
  const [disposal, setDisposal] = useState<DisposalInput>({ disposalType: 'SALE', disposalDate: todayStr(), disposalAmount: 0, gstType: 'NONE', gstAmount: 0 });
  const [disposalPreview, setDisposalPreview] = useState<DisposalPreview | null>(null);
  const [disposalError, setDisposalError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setData(await fixedAssetsApi.getAssetById(id));
    } catch (err) {
      toast.error(errMsg(err, 'Failed to load the asset'));
    } finally {
      setLoading(false);
    }
  }, [id, toast]);

  useEffect(() => {
    load();
    bankReconApi.getCashBankLedgers().then(setLedgers).catch(() => {});
    getSuppliers().then((r) => setSuppliers(r.data || [])).catch(() => {});
  }, [load]);

  // Live disposal figures
  useEffect(() => {
    if (!disposeOpen) return;
    const t = setTimeout(() => {
      fixedAssetsApi
        .previewDisposal(id, disposal)
        .then((p) => {
          setDisposalPreview(p);
          setDisposalError(null);
        })
        .catch((err) => {
          setDisposalPreview(null);
          setDisposalError(errMsg(err, 'Check the disposal details'));
        });
    }, 300);
    return () => clearTimeout(t);
  }, [disposeOpen, disposal, id]);

  const run = async (fn: () => Promise<{ message?: string }>, fallback: string, after?: () => void) => {
    try {
      setBusy(true);
      const res = await fn();
      if (res?.message) toast.success(res.message);
      after?.();
      await load();
    } catch (err) {
      toast.error(errMsg(err, fallback));
    } finally {
      setBusy(false);
    }
  };

  if (loading && !data) {
    return (
      <AppLayout>
        <div className="py-24 text-center text-slate-400 text-sm">
          <i className="ri-loader-4-line animate-spin text-2xl text-[#4f46e5] mr-2" /> Loading asset…
        </div>
      </AppLayout>
    );
  }
  if (!data) {
    return (
      <AppLayout>
        <div className="p-6 text-center">
          <p className="text-slate-600">Asset not found.</p>
          <button onClick={() => navigate('/finance/assets')} className={`${primaryBtn} mt-4`}>
            Back to Fixed Assets
          </button>
        </div>
      </AppLayout>
    );
  }

  const a = data.asset;
  const disposed = ['DISPOSED', 'SCRAPPED', 'WRITTEN_OFF'].includes(a.status);
  const gst = a.gst_paid;
  const register: [string, React.ReactNode][] = [
    ['Asset ID', <span className="font-mono">{a.asset_code}</span>],
    ['Category', a.category_name],
    ['Purchase Date', fmtDate(a.purchase_date)],
    ['Put to Use', fmtDate(a.depreciation_start_date)],
    ['Acquired', MODE_LABEL[a.capitalization_mode]],
    ['Vendor', a.vendor_name || '—'],
    ['Purchase Invoice', a.purchase_invoice_number || '—'],
    ['CAPEX Request', a.capex_request_number || '—'],
    ['Serial Number', a.serial_number || '—'],
    ['Make / Model', a.make_model || '—'],
    ['Location', [a.warehouse_name, a.department, a.location_detail].filter(Boolean).join(' · ') || '—'],
    ['Custodian', a.custodian_name || a.custodian_email || '—'],
    ['Production Machine', a.machine_name || '—'],
    ['Warranty Till', fmtDate(a.warranty_expiry)],
    ['Insurance', a.insurance_company ? `${a.insurance_company}${a.insurance_policy_no ? ` · ${a.insurance_policy_no}` : ''} · till ${fmtDate(a.insurance_expiry)}` : '—'],
    ['Condition', a.condition],
  ];

  const saveTransfer = () =>
    run(() => fixedAssetsApi.updateAsset(id, transfer), 'Transfer failed', () => setTransferOpen(false));

  const saveMaintenance = () => {
    if (!maint.description.trim()) return toast.error('Describe the work done');
    if (!(maint.amount > 0)) return toast.error('Enter the cost');
    if (maint.paymentMode === 'VENDOR_CREDIT' && !maint.vendorId) return toast.error('Select the vendor');
    run(() => fixedAssetsApi.addMaintenance(id, maint), 'Failed to record maintenance', () => setMaintOpen(false));
  };

  const saveDisposal = () =>
    run(() => fixedAssetsApi.disposeAsset(id, disposal), 'Disposal failed', () => setDisposeOpen(false));

  const maintGst = maint.gstType === 'NONE' ? 0 : Number(maint.gstAmount || 0);
  const maintDebit = Number(maint.amount || 0) + (maint.itcEligible ? 0 : maintGst);
  const payLabel = (mode: string, accountId?: string, vendorId?: string) =>
    mode === 'VENDOR_CREDIT'
      ? `Accounts Payable — ${suppliers.find((s) => s.id === vendorId)?.name || 'vendor'}`
      : ledgers.find((l) => l.id === accountId)?.name || (mode === 'CASH' ? 'Cash in Hand' : 'Default bank account');

  return (
    <AppLayout>
      <div className="p-6 max-w-[1300px] mx-auto space-y-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <button onClick={() => navigate('/finance/assets')} className="w-9 h-9 flex items-center justify-center rounded-lg border border-[#e2e8f0] bg-white text-slate-500 hover:bg-slate-50 cursor-pointer">
              <i className="ri-arrow-left-line" />
            </button>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl font-bold text-[#1e293b]">{a.asset_name}</h1>
                <StatusBadge status={a.status} />
              </div>
              <p className="text-sm text-slate-500 mt-0.5 font-mono">
                {a.asset_code} · {a.category_name}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {!disposed && (
              <>
                <button onClick={() => navigate(`/finance/assets/new?edit=${id}`)} className={secondaryBtn}>
                  <i className="ri-pencil-line" /> Edit
                </button>
                <button
                  onClick={() => {
                    setTransfer({ department: a.department || '', locationDetail: a.location_detail || '', custodianName: a.custodian_name || '', transferRemarks: '', movementDate: todayStr() });
                    setTransferOpen(true);
                  }}
                  className={secondaryBtn}
                >
                  <i className="ri-map-pin-line" /> Transfer
                </button>
                <button
                  onClick={() => {
                    setMaint({ ...emptyMaintenance });
                    setMaintOpen(true);
                  }}
                  className={secondaryBtn}
                >
                  <i className="ri-tools-line" /> Repair / Upgrade
                </button>
                {(a.status === 'ACTIVE' || a.status === 'UNDER_MAINTENANCE') && (
                  <button
                    onClick={() => run(() => fixedAssetsApi.setStatus(id, a.status === 'ACTIVE' ? 'UNDER_MAINTENANCE' : 'ACTIVE'), 'Status change failed')}
                    disabled={busy}
                    className={secondaryBtn}
                  >
                    <i className={a.status === 'ACTIVE' ? 'ri-pause-circle-line' : 'ri-play-circle-line'} />
                    {a.status === 'ACTIVE' ? 'Under Maintenance' : 'Back in Use'}
                  </button>
                )}
                <button
                  onClick={() => {
                    setDisposal({ disposalType: 'SALE', disposalDate: todayStr(), disposalAmount: 0, gstType: 'NONE', gstAmount: 0 });
                    setDisposeOpen(true);
                  }}
                  className={dangerBtn}
                >
                  <i className="ri-delete-bin-6-line" /> Sell / Scrap / Write-off
                </button>
              </>
            )}
            {disposed && (
              <button
                onClick={() =>
                  setConfirm({
                    title: 'Cancel Disposal',
                    message: 'Undo this disposal? The asset returns to use and the disposal entry is removed from the books.',
                    onConfirm: () => {
                      setConfirm(null);
                      run(() => fixedAssetsApi.cancelDisposal(id), 'Failed to cancel disposal');
                    },
                  })
                }
                className={secondaryBtn}
              >
                <i className="ri-arrow-go-back-line" /> Cancel Disposal
              </button>
            )}
          </div>
        </div>

        {/* Value cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          {[
            { label: 'Capitalised Cost', value: money(a.total_capitalized_cost), sub: a.capital_improvements ? `incl. ${money(a.capital_improvements)} improvements` : `Purchase ${money(a.purchase_cost)} + ${money(a.additional_cost)}` },
            { label: 'Accumulated Depreciation', value: money(a.accumulated_depreciation), sub: a.depreciated_upto ? `Up to ${fmtDate(a.depreciated_upto)}` : 'Not yet depreciated', cls: 'text-amber-700' },
            { label: 'Net Book Value', value: money(disposed ? a.total_capitalized_cost - a.accumulated_depreciation : a.current_book_value), sub: disposed ? 'At disposal' : 'Cost − accumulated depreciation', cls: 'text-[#4f46e5]' },
            {
              label: 'Depreciation',
              value: a.depreciation_method === 'NONE' ? 'Not depreciated' : a.depreciation_method === 'SLM' ? `SLM · ${a.useful_life_years ?? '—'} yrs` : `WDV · ${a.depreciation_rate}%`,
              sub: a.depreciation_method === 'NONE' ? '' : `Salvage ${money(a.scrap_value)}`,
            },
            disposed
              ? { label: a.status === 'DISPOSED' ? 'Sold For' : 'Recovered', value: money(a.disposal_amount), sub: `${(a.profit_loss_on_disposal || 0) >= 0 ? 'Profit' : 'Loss'} ${money(Math.abs(a.profit_loss_on_disposal || 0))} on ${fmtDate(a.disposal_date)}`, cls: (a.profit_loss_on_disposal || 0) >= 0 ? 'text-green-700' : 'text-red-600' }
              : { label: 'GST on Purchase', value: money(gst), sub: gst ? (a.itc_eligible ? 'Input tax credit claimed' : 'Capitalised (no ITC)') : '—' },
          ].map((c) => (
            <div key={c.label} className={`${cardCls} p-4`}>
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{c.label}</p>
              <p className={`text-lg font-bold mt-1 ${c.cls || 'text-[#1e293b]'}`}>{c.value}</p>
              <p className="text-[11px] text-slate-400 mt-0.5">{c.sub}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1 w-fit border border-[#e2e8f0]">
          {([
            ['overview', 'Asset Register', 'ri-file-list-3-line'],
            ['depreciation', 'Depreciation', 'ri-line-chart-line'],
            ['maintenance', `Maintenance (${data.maintenance.length})`, 'ri-tools-line'],
            ['history', 'History', 'ri-history-line'],
            ['accounting', `Accounting (${data.vouchers.length})`, 'ri-book-2-line'],
          ] as [Tab, string, string][]).map(([key, label, icon]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap ${tab === key ? 'bg-white text-[#1e293b] shadow-sm' : 'text-slate-600 hover:text-[#1e293b]'}`}
            >
              <i className={`${icon} text-sm`} /> {label}
            </button>
          ))}
        </div>

        {tab === 'overview' && (
          <div className={`${cardCls} p-5`}>
            <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 text-sm">
              {register.map(([k, v]) => (
                <div key={k}>
                  <dt className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{k}</dt>
                  <dd className="text-[#1e293b] mt-0.5">{v}</dd>
                </div>
              ))}
            </dl>
            {a.description && <p className="text-sm text-slate-600 mt-4 pt-4 border-t border-slate-100">{a.description}</p>}
            {disposed && (
              <div className="mt-4 pt-4 border-t border-slate-100 text-sm text-slate-600">
                <b>{a.disposal_type === 'SALE' ? 'Sold' : a.disposal_type === 'SCRAP' ? 'Scrapped' : 'Written off'}</b> on {fmtDate(a.disposal_date)}
                {a.disposal_buyer ? ` to ${a.disposal_buyer}` : ''}
                {a.disposal_reason ? ` — ${a.disposal_reason}` : ''}. Depreciation up to the disposal date: {money(a.disposal_depreciation)}.
              </div>
            )}
          </div>
        )}

        {tab === 'depreciation' && (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <div className={`${cardCls} overflow-hidden`}>
              <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/60">
                <p className="text-sm font-semibold text-slate-800">Projected Schedule</p>
                <p className="text-xs text-slate-400">
                  {a.next_depreciation_date ? `From ${fmtDate(a.next_depreciation_date)}, by financial year` : 'No further depreciation'}
                </p>
              </div>
              <div className="max-h-[420px] overflow-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className={`${thCls} text-left`}>FY</th>
                      <th className={`${thCls} text-right`}>Opening</th>
                      <th className={`${thCls} text-right`}>Depreciation</th>
                      <th className={`${thCls} text-right`}>Closing</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {data.projectedSchedule.map((r) => (
                      <tr key={r.financialYear}>
                        <td className="px-4 py-2 text-slate-700">FY {r.financialYear}</td>
                        <td className="px-4 py-2 text-right font-mono">{money(r.openingBookValue)}</td>
                        <td className="px-4 py-2 text-right font-mono text-red-600">{money(r.depreciationAmount)}</td>
                        <td className="px-4 py-2 text-right font-mono font-semibold">{money(r.closingBookValue)}</td>
                      </tr>
                    ))}
                    {data.projectedSchedule.length === 0 && (
                      <tr>
                        <td colSpan={4} className="px-4 py-8 text-center text-slate-400">
                          Nothing to project.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className={`${cardCls} overflow-hidden`}>
              <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/60">
                <p className="text-sm font-semibold text-slate-800">Posted Depreciation</p>
                <p className="text-xs text-slate-400">Monthly runs charged to this asset</p>
              </div>
              <div className="max-h-[420px] overflow-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className={`${thCls} text-left`}>Period</th>
                      <th className={`${thCls} text-right`}>Opening</th>
                      <th className={`${thCls} text-right`}>Depreciation</th>
                      <th className={`${thCls} text-right`}>Closing</th>
                      <th className={`${thCls} text-left`}>Voucher</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {data.depreciationHistory.map((l) => (
                      <tr key={l.id}>
                        <td className="px-4 py-2 text-slate-700 whitespace-nowrap">
                          {fmtDate(l.period_from)} – {fmtDate(l.period_end)}
                        </td>
                        <td className="px-4 py-2 text-right font-mono">{money(l.opening_book_value)}</td>
                        <td className="px-4 py-2 text-right font-mono text-red-600">{money(l.depreciation_amount)}</td>
                        <td className="px-4 py-2 text-right font-mono font-semibold">{money(l.closing_book_value)}</td>
                        <td className="px-4 py-2 font-mono text-[#4f46e5]">{l.voucher_number || '—'}</td>
                      </tr>
                    ))}
                    {data.depreciationHistory.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-4 py-8 text-center text-slate-400">
                          No depreciation posted yet — run it from Fixed Assets → Run Depreciation.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {tab === 'maintenance' && (
          <div className="space-y-4">
            <div className={`${cardCls} overflow-x-auto`}>
              <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/60 flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-800">Repairs & Improvements</p>
                  <p className="text-xs text-slate-400">Revenue repairs go to P&L; capital improvements add to the asset cost</p>
                </div>
                {!disposed && (
                  <button onClick={() => { setMaint({ ...emptyMaintenance }); setMaintOpen(true); }} className={primaryBtn}>
                    <i className="ri-add-line" /> Record
                  </button>
                )}
              </div>
              <table className="w-full text-xs min-w-[800px]">
                <thead className="bg-slate-50">
                  <tr>
                    <th className={`${thCls} text-left`}>Date</th>
                    <th className={`${thCls} text-left`}>Type</th>
                    <th className={`${thCls} text-left`}>Work</th>
                    <th className={`${thCls} text-left`}>Vendor / Paid</th>
                    <th className={`${thCls} text-right`}>Amount</th>
                    <th className={`${thCls} text-left`}>Voucher</th>
                    <th className={thCls} />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.maintenance.map((m) => (
                    <tr key={m.id}>
                      <td className="px-4 py-2 whitespace-nowrap">{fmtDate(m.maintenance_date)}</td>
                      <td className="px-4 py-2">
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${m.maintenance_type === 'CAPITAL' ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                          {m.maintenance_type === 'CAPITAL' ? 'Capital' : 'Revenue'}
                        </span>
                      </td>
                      <td className="px-4 py-2">
                        <p className="text-[#1e293b] font-medium">{m.description}</p>
                        <p className="text-[10px] text-slate-400">
                          {m.work_category || ''}
                          {m.life_extension_years ? ` · life +${m.life_extension_years} yr` : ''}
                          {m.downtime_days ? ` · ${m.downtime_days} day(s) down` : ''}
                        </p>
                      </td>
                      <td className="px-4 py-2 text-slate-600">{m.vendor_name || m.payment_account_name || m.payment_mode}</td>
                      <td className="px-4 py-2 text-right font-mono font-semibold">
                        {money(m.amount)}
                        {m.gst_amount > 0 && <p className="text-[10px] text-slate-400 font-normal">+ GST {money(m.gst_amount)}</p>}
                      </td>
                      <td className="px-4 py-2 font-mono text-[#4f46e5]">{m.voucher_number || '—'}</td>
                      <td className="px-4 py-2 text-right">
                        <button
                          onClick={() =>
                            setConfirm({
                              title: 'Remove Maintenance Entry',
                              message: `Remove "${m.description}"? Its accounting entry is reversed${m.maintenance_type === 'CAPITAL' ? ' and the asset cost reduced' : ''}.`,
                              onConfirm: () => {
                                setConfirm(null);
                                run(() => fixedAssetsApi.deleteMaintenance(m.id), 'Failed to remove the entry');
                              },
                            })
                          }
                          className="p-1 rounded text-slate-400 hover:text-red-600 hover:bg-red-50 cursor-pointer"
                          title="Remove"
                        >
                          <i className="ri-delete-bin-line" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {data.maintenance.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-slate-400">
                        No repairs or improvements recorded.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {a.machine_id && (
              <div className={`${cardCls} overflow-x-auto`}>
                <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/60">
                  <p className="text-sm font-semibold text-slate-800">Machine Maintenance Work Orders</p>
                  <p className="text-xs text-slate-400">From Manufacturing → Maintenance for {a.machine_name}</p>
                </div>
                <table className="w-full text-xs min-w-[700px]">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className={`${thCls} text-left`}>Work Order</th>
                      <th className={`${thCls} text-left`}>Task</th>
                      <th className={`${thCls} text-left`}>Scheduled</th>
                      <th className={`${thCls} text-left`}>Status</th>
                      <th className={`${thCls} text-left`}>Findings</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {data.machineWorkOrders.map((w) => (
                      <tr key={w.mwo_number}>
                        <td className="px-4 py-2 font-mono text-[#4f46e5]">{w.mwo_number}</td>
                        <td className="px-4 py-2">{w.task_name}</td>
                        <td className="px-4 py-2">{fmtDate(w.scheduled_date)}</td>
                        <td className="px-4 py-2">{w.status}</td>
                        <td className="px-4 py-2 text-slate-500">{w.findings || '—'}</td>
                      </tr>
                    ))}
                    {data.machineWorkOrders.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-4 py-6 text-center text-slate-400">
                          No maintenance work orders for this machine.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {tab === 'history' && (
          <div className={`${cardCls} p-5`}>
            <div className="space-y-3">
              {data.movements.map((m) => (
                <div key={m.id} className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-indigo-50 text-[#4f46e5] flex items-center justify-center shrink-0">
                    <i className="ri-git-commit-line" />
                  </div>
                  <div className="flex-1 border-b border-slate-100 pb-3">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm font-semibold text-[#1e293b]">{m.movement_type.replace(/_/g, ' ')}</p>
                      <p className="text-xs text-slate-400">{fmtDate(m.movement_date)}</p>
                    </div>
                    {(m.from_location || m.to_location) && (
                      <p className="text-xs text-slate-600">
                        {m.from_location || '—'} → {m.to_location || '—'}
                      </p>
                    )}
                    {m.remarks && <p className="text-xs text-slate-500">{m.remarks}</p>}
                    <p className="text-[11px] text-slate-400">
                      {m.amount ? `${money(m.amount)} · ` : ''}
                      {m.performed_by_email || ''}
                    </p>
                  </div>
                </div>
              ))}
              {data.movements.length === 0 && <p className="text-sm text-slate-400">No history yet.</p>}
            </div>
          </div>
        )}

        {tab === 'accounting' && (
          <div className={`${cardCls} overflow-x-auto`}>
            <table className="w-full text-xs min-w-[700px]">
              <thead className="bg-slate-50">
                <tr>
                  <th className={`${thCls} text-left`}>Date</th>
                  <th className={`${thCls} text-left`}>Voucher</th>
                  <th className={`${thCls} text-left`}>Type</th>
                  <th className={`${thCls} text-left`}>Narration</th>
                  <th className={`${thCls} text-right`}>Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.vouchers.map((v) => (
                  <tr key={v.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/finance/journal/${v.id}`)}>
                    <td className="px-4 py-2">{fmtDate(v.voucher_date)}</td>
                    <td className="px-4 py-2 font-mono text-[#4f46e5]">{v.voucher_number}</td>
                    <td className="px-4 py-2">{v.voucher_type.replace(/_/g, ' ')}</td>
                    <td className="px-4 py-2 text-slate-600">{v.narration}</td>
                    <td className="px-4 py-2 text-right font-mono font-semibold">{money(v.total_debit)}</td>
                  </tr>
                ))}
                {data.vouchers.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-slate-400">
                      No vouchers yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <p className="text-[11px] text-slate-400 px-4 py-2">Monthly depreciation vouchers are listed on the Depreciation tab.</p>
          </div>
        )}
      </div>

      {transferOpen && (
        <Modal
          title="Transfer Asset"
          subtitle="Change location, department or custodian — recorded in the asset history"
          onClose={() => setTransferOpen(false)}
          footer={
            <>
              <button onClick={() => setTransferOpen(false)} className={secondaryBtn}>Cancel</button>
              <button onClick={saveTransfer} disabled={busy} className={primaryBtn}>Save Transfer</button>
            </>
          }
        >
          <div className="grid grid-cols-2 gap-3">
            <Field label="Department">
              <input value={transfer.department} onChange={(e) => setTransfer((p) => ({ ...p, department: e.target.value }))} className={inputCls} />
            </Field>
            <Field label="Location">
              <input value={transfer.locationDetail} onChange={(e) => setTransfer((p) => ({ ...p, locationDetail: e.target.value }))} className={inputCls} />
            </Field>
            <Field label="Custodian">
              <input value={transfer.custodianName} onChange={(e) => setTransfer((p) => ({ ...p, custodianName: e.target.value }))} className={inputCls} />
            </Field>
            <Field label="Date">
              <input type="date" value={transfer.movementDate} max={todayStr()} onChange={(e) => setTransfer((p) => ({ ...p, movementDate: e.target.value }))} className={inputCls} />
            </Field>
          </div>
          <Field label="Remarks">
            <input value={transfer.transferRemarks} onChange={(e) => setTransfer((p) => ({ ...p, transferRemarks: e.target.value }))} className={inputCls} />
          </Field>
        </Modal>
      )}

      {maintOpen && (
        <Modal
          title="Repair / Upgrade"
          subtitle="Revenue repair → expense · Capital improvement → added to asset cost"
          size="max-w-2xl"
          onClose={() => setMaintOpen(false)}
          footer={
            <>
              <button onClick={() => setMaintOpen(false)} className={secondaryBtn}>Cancel</button>
              <button onClick={saveMaintenance} disabled={busy} className={primaryBtn}>Record & Post</button>
            </>
          }
        >
          <div className="grid grid-cols-2 gap-2">
            {([
              ['REVENUE', 'Revenue repair (OPEX)', 'Routine service, spare parts, cleaning — expensed'],
              ['CAPITAL', 'Capital improvement (CAPEX)', 'Overhaul, capacity or life extension — capitalised'],
            ] as const).map(([value, title, hint]) => (
              <label key={value} className={`p-3 rounded-lg border cursor-pointer ${maint.maintenanceType === value ? 'border-indigo-300 bg-indigo-50/50' : 'border-[#e2e8f0] hover:bg-slate-50'}`}>
                <span className="flex items-center gap-2 text-sm font-semibold text-[#1e293b]">
                  <input
                    type="radio"
                    checked={maint.maintenanceType === value}
                    onChange={() => setMaint((p) => ({ ...p, maintenanceType: value, workCategory: WORK_CATEGORIES[value][0] }))}
                  />
                  {title}
                </span>
                <span className="text-[11px] text-slate-500 block mt-0.5 pl-5">{hint}</span>
              </label>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Date" required>
              <input type="date" value={maint.maintenanceDate} max={todayStr()} onChange={(e) => setMaint((p) => ({ ...p, maintenanceDate: e.target.value }))} className={inputCls} />
            </Field>
            <Field label="Work Type">
              <select value={maint.workCategory} onChange={(e) => setMaint((p) => ({ ...p, workCategory: e.target.value }))} className={inputCls}>
                {WORK_CATEGORIES[maint.maintenanceType].map((w) => (
                  <option key={w}>{w}</option>
                ))}
              </select>
            </Field>
            <Field label="Cost (excl. GST)" required>
              <input type="number" min={0} value={maint.amount || ''} onChange={(e) => setMaint((p) => ({ ...p, amount: Number(e.target.value) }))} className={inputCls} />
            </Field>
          </div>
          <Field label="Description" required>
            <input value={maint.description} onChange={(e) => setMaint((p) => ({ ...p, description: e.target.value }))} placeholder="What was done" className={inputCls} />
          </Field>
          <GstFields gstType={maint.gstType || 'NONE'} gstAmount={maint.gstAmount || 0} itcEligible={maint.itcEligible} base={maint.amount} onChange={(p) => setMaint((m) => ({ ...m, ...p }))} />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Paid By">
              <select value={maint.paymentMode} onChange={(e) => setMaint((p) => ({ ...p, paymentMode: e.target.value as MaintenanceInput['paymentMode'], paymentAccountId: undefined }))} className={inputCls}>
                <option value="BANK">Bank</option>
                <option value="CASH">Cash</option>
                <option value="VENDOR_CREDIT">On credit (vendor)</option>
              </select>
            </Field>
            {maint.paymentMode === 'VENDOR_CREDIT' ? (
              <Field label="Vendor" required>
                <select value={maint.vendorId || ''} onChange={(e) => setMaint((p) => ({ ...p, vendorId: e.target.value }))} className={inputCls}>
                  <option value="">Select…</option>
                  {suppliers.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </Field>
            ) : (
              <Field label="From Account">
                <select value={maint.paymentAccountId || ''} onChange={(e) => setMaint((p) => ({ ...p, paymentAccountId: e.target.value || undefined }))} className={inputCls}>
                  <option value="">{maint.paymentMode === 'CASH' ? 'Cash in Hand' : 'Default bank account'}</option>
                  {ledgers.filter((l) => (maint.paymentMode === 'CASH' ? l.kind === 'CASH' : l.kind === 'BANK')).map((l) => (
                    <option key={l.id} value={l.id}>{l.name}</option>
                  ))}
                </select>
              </Field>
            )}
            <Field label="Bill / Ref No.">
              <input value={maint.referenceNo || ''} onChange={(e) => setMaint((p) => ({ ...p, referenceNo: e.target.value }))} className={inputCls} />
            </Field>
            {maint.maintenanceType === 'CAPITAL' && (
              <Field label="Extends Life By (years)" hint="Remaining depreciation is recalculated">
                <input type="number" min={0} step="0.5" value={maint.lifeExtensionYears || ''} onChange={(e) => setMaint((p) => ({ ...p, lifeExtensionYears: Number(e.target.value) }))} className={inputCls} />
              </Field>
            )}
            <Field label="Downtime (days)">
              <input type="number" min={0} value={maint.downtimeDays || ''} onChange={(e) => setMaint((p) => ({ ...p, downtimeDays: Number(e.target.value) }))} className={inputCls} />
            </Field>
          </div>
          {a.status === 'ACTIVE' && (
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input type="checkbox" checked={!!maint.markUnderMaintenance} onChange={(e) => setMaint((p) => ({ ...p, markUnderMaintenance: e.target.checked }))} className="w-4 h-4 rounded" />
              Mark the asset as Under Maintenance
            </label>
          )}
          <EntryPreview
            lines={[
              { side: 'DR', account: maint.maintenanceType === 'CAPITAL' ? `${a.category_name} (asset)` : 'Repairs & Maintenance', amount: maintDebit },
              ...(maint.itcEligible && maintGst ? [{ side: 'DR' as const, account: 'Input GST', amount: maintGst }] : []),
              { side: 'CR', account: payLabel(maint.paymentMode, maint.paymentAccountId, maint.vendorId), amount: Number(maint.amount || 0) + maintGst },
            ]}
          />
        </Modal>
      )}

      {disposeOpen && (
        <Modal
          title="Dispose Asset"
          subtitle="Depreciation up to the disposal date is charged, then the asset is removed from the books"
          size="max-w-2xl"
          onClose={() => setDisposeOpen(false)}
          footer={
            <>
              <button onClick={() => setDisposeOpen(false)} className={secondaryBtn}>Cancel</button>
              <button onClick={saveDisposal} disabled={busy || !disposalPreview} className={primaryBtn}>Confirm Disposal</button>
            </>
          }
        >
          <div className="grid grid-cols-3 gap-2">
            {([
              ['SALE', 'Sell', 'ri-money-rupee-circle-line'],
              ['SCRAP', 'Scrap', 'ri-recycle-line'],
              ['WRITE_OFF', 'Write-off (lost / destroyed)', 'ri-error-warning-line'],
            ] as const).map(([value, label, icon]) => (
              <label key={value} className={`flex items-center gap-2 p-2.5 rounded-lg border text-sm cursor-pointer ${disposal.disposalType === value ? 'border-indigo-300 bg-indigo-50/50 font-semibold' : 'border-[#e2e8f0] hover:bg-slate-50'}`}>
                <input type="radio" checked={disposal.disposalType === value} onChange={() => setDisposal((p) => ({ ...p, disposalType: value, gstType: value === 'WRITE_OFF' ? 'NONE' : p.gstType, gstAmount: value === 'WRITE_OFF' ? 0 : p.gstAmount }))} />
                <i className={icon} /> {label}
              </label>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Date" required>
              <input type="date" value={disposal.disposalDate} max={todayStr()} onChange={(e) => setDisposal((p) => ({ ...p, disposalDate: e.target.value }))} className={inputCls} />
            </Field>
            <Field label={disposal.disposalType === 'SALE' ? 'Sale Price (excl. GST)' : disposal.disposalType === 'SCRAP' ? 'Scrap Value Received' : 'Insurance / Amount Recovered'} required={disposal.disposalType === 'SALE'}>
              <input type="number" min={0} value={disposal.disposalAmount || ''} onChange={(e) => setDisposal((p) => ({ ...p, disposalAmount: Number(e.target.value) }))} className={inputCls} />
            </Field>
            <Field label="Received In">
              <select value={disposal.bankAccountId || ''} onChange={(e) => setDisposal((p) => ({ ...p, bankAccountId: e.target.value || undefined }))} className={inputCls}>
                <option value="">Default bank account</option>
                {ledgers.map((l) => (
                  <option key={l.id} value={l.id}>{l.name}</option>
                ))}
              </select>
            </Field>
          </div>
          {disposal.disposalType !== 'WRITE_OFF' && (
            <GstFields gstType={disposal.gstType || 'NONE'} gstAmount={disposal.gstAmount || 0} showItc={false} base={disposal.disposalAmount} onChange={(p) => setDisposal((d) => ({ ...d, gstType: p.gstType ?? d.gstType, gstAmount: p.gstAmount ?? d.gstAmount }))} />
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label={disposal.disposalType === 'SALE' ? 'Buyer' : 'Handled By'}>
              <input value={disposal.disposalBuyer || ''} onChange={(e) => setDisposal((p) => ({ ...p, disposalBuyer: e.target.value }))} className={inputCls} />
            </Field>
            <Field label="Reason">
              <input value={disposal.disposalReason || ''} onChange={(e) => setDisposal((p) => ({ ...p, disposalReason: e.target.value }))} placeholder="End of life, upgrade, damage…" className={inputCls} />
            </Field>
          </div>
          {disposalError && <p className="text-xs text-red-600">{disposalError}</p>}
          {disposalPreview && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                {[
                  ['Cost', money(disposalPreview.cost)],
                  ['Depreciation to date', money(disposalPreview.pendingDepreciation)],
                  ['Net Book Value', money(disposalPreview.netBookValue)],
                  [disposalPreview.profitLoss >= 0 ? 'Profit on disposal' : 'Loss on disposal', money(Math.abs(disposalPreview.profitLoss))],
                ].map(([k, v], i) => (
                  <div key={k} className={`rounded-lg border p-2 ${i === 3 ? (disposalPreview.profitLoss >= 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200') : 'bg-slate-50 border-[#e2e8f0]'}`}>
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{k}</p>
                    <p className="font-bold text-[#1e293b]">{v}</p>
                  </div>
                ))}
              </div>
              <EntryPreview
                lines={[
                  { side: 'DR', account: 'Depreciation Expense (to disposal date)', amount: disposalPreview.pendingDepreciation },
                  { side: 'CR', account: 'Accumulated Depreciation', amount: disposalPreview.pendingDepreciation },
                  { side: 'DR', account: 'Accumulated Depreciation (written back)', amount: disposalPreview.accumulatedDepreciation },
                  { side: 'DR', account: ledgers.find((l) => l.id === disposal.bankAccountId)?.name || 'Bank', amount: disposalPreview.proceeds + disposalPreview.gst.total },
                  ...(disposalPreview.profitLoss < 0 ? [{ side: 'DR' as const, account: `Loss on ${disposal.disposalType === 'SALE' ? 'sale' : disposal.disposalType === 'SCRAP' ? 'scrapping' : 'write-off'}`, amount: -disposalPreview.profitLoss }] : []),
                  { side: 'CR', account: `${a.category_name} (asset cost)`, amount: disposalPreview.cost },
                  ...(disposalPreview.gst.total ? [{ side: 'CR' as const, account: 'Output GST', amount: disposalPreview.gst.total }] : []),
                  ...(disposalPreview.profitLoss > 0 ? [{ side: 'CR' as const, account: 'Profit on Sale of Assets', amount: disposalPreview.profitLoss }] : []),
                ]}
              />
            </>
          )}
        </Modal>
      )}

      <ConfirmDialog
        open={!!confirm}
        title={confirm?.title || ''}
        message={confirm?.message || ''}
        variant="warning"
        onConfirm={() => confirm?.onConfirm()}
        onCancel={() => setConfirm(null)}
      />
    </AppLayout>
  );
}
